import { apiClient } from './apiClient'
import { environment } from '../../config/environment'
import { StorageService } from '../firebase/storageService'
import type { Product, ProductImage, ProductImageType } from '../../types/product'
import type { PaginatedResponse } from '../../types/common'
import { logger } from '../../utils/logger'

export interface ProductsFilters {
  query?: string
  search?: string
  toValidate?: boolean | null
  page?: number
  limit?: number
  sortBy?: string
}

export class ProductsService {
  /**
   * Récupérer la liste des produits avec pagination
   */
  static async getProducts(filters: ProductsFilters = {}): Promise<PaginatedResponse<Product>> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await ProductsService.getMockProducts(filters)
    }
    
    const params = new URLSearchParams()
    
    // Use 'search' for the API (Flutter uses 'search' not 'query')
    if (filters.query || filters.search) {
      params.append('search', filters.query || filters.search || '')
    }
    if (filters.toValidate !== null && filters.toValidate !== undefined) {
      params.append('toValidate', filters.toValidate.toString())
    }
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.sortBy) params.append('sort', filters.sortBy)
    
    const queryString = params.toString()
    const url = `/v1/products${queryString ? `?${queryString}` : ''}`
    
    logger.log('📦 Fetching products from API...', url)
    const response = await apiClient.get<any>(url)
    logger.log('✅ Products received:', response)
    
    // Map the API response to our PaginatedResponse type
    // The API returns 'products' array, not 'data'
    return {
      data: response.products || [],
      currentPage: response.currentPage,
      lastPage: response.lastPage,
      firstPage: response.firstPage,
      pageSize: response.pageSize,
      totalRecords: response.totalRecords,
    }
  }

  /**
   * Récupérer un produit par son ID
   */
  static async getProduct(id: string): Promise<Product> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await ProductsService.getMockProduct(id)
    }
    
    const product = await apiClient.get<Product>(`/v1/products/${id}`)
    
    // Update image URLs with Firebase Storage download URLs
    if (product.images && product.images.length > 0) {
      try {
        const updatedImages = await Promise.all(
          product.images.map(async (image: any) => {
            // Normalize imageType from uppercase to lowercase
            const normalizedImage: ProductImage = {
              ...image,
              imageType: image.imageType?.toLowerCase() as ProductImageType
            }
            
            // If imageUrl is a relative path (not starting with http), get the download URL
            if (normalizedImage.imageUrl && !normalizedImage.imageUrl.startsWith('http')) {
              try {
                const downloadUrl = await StorageService.getDownloadURL(normalizedImage.imageUrl)
                return { ...normalizedImage, imageUrl: downloadUrl }
              } catch (error) {
                logger.error(`Failed to get download URL for image: ${normalizedImage.imageUrl}`, error)
                return normalizedImage
              }
            }
            return normalizedImage
          })
        )
        product.images = updatedImages
      } catch (error) {
        logger.error('Failed to update image URLs:', error)
      }
    }
    
    return product
  }

  /**
   * Créer un nouveau produit
   */
  static async createProduct(product: Partial<Product>): Promise<Product> {
    const createdProduct = await apiClient.post<Product>('/v1/products', product)
    return createdProduct
  }

  /**
   * Mettre à jour un produit
   */
  static async updateProduct(id: string, product: Partial<Product>): Promise<Product> {
    const updatedProduct = await apiClient.put<Product>(`/v1/products/${id}`, product)
    return updatedProduct
  }

  /**
   * Supprimer un produit
   */
  static async deleteProduct(id: string): Promise<void> {
    await apiClient.delete(`/v1/products/${id}`)
  }

  /**
   * Extraire les ingrédients d'un produit (disponible uniquement pour les produits existants)
   */
  static async extractIngredients(id: string): Promise<void> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      // Simulation for development
      return new Promise((resolve) => {
        setTimeout(() => resolve(), 2000)
      })
    }
    
    await apiClient.post(`/v1/products/${id}/extract-ingredients`)
  }

  /**
   * Données mockées pour le mode fake
   */
  private static async getMockProducts(filters: ProductsFilters): Promise<PaginatedResponse<Product>> {
    logger.log('🎭 Using mock products data')
    
    const allProducts: Product[] = Array.from({ length: 50 }, (_, i) => ({
      uuid: `product-${i + 1}`,
      brandUUID: `brand-${(i % 5) + 1}`,
      brandName: `Brand ${(i % 5) + 1}`,
      categoryId: (i % 3) + 1,
      categoryName: `Category ${(i % 3) + 1}`,
      subCategoryId: (i % 2) + 1,
      subCategoryName: `SubCategory ${(i % 2) + 1}`,
      toValidate: i % 3 === 0,
      createdAt: new Date(Date.now() - i * 86400000).toISOString(),
      updatedAt: new Date(Date.now() - i * 43200000).toISOString(),
    }))
    
    // Apply filters
    let filteredProducts = [...allProducts]
    
    if (filters.query) {
      const query = filters.query.toLowerCase()
      filteredProducts = filteredProducts.filter(p => 
        p.brandName?.toLowerCase().includes(query) ||
        p.categoryName?.toLowerCase().includes(query)
      )
    }
    
    if (filters.toValidate !== null && filters.toValidate !== undefined) {
      filteredProducts = filteredProducts.filter(p => p.toValidate === filters.toValidate)
    }
    
    // Apply sorting
    if (filters.sortBy) {
      const isDesc = filters.sortBy.startsWith('-')
      const field = isDesc ? filters.sortBy.slice(1) : filters.sortBy
      
      filteredProducts.sort((a, b) => {
        const aVal = (a as any)[field] || ''
        const bVal = (b as any)[field] || ''
        const result = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
        return isDesc ? -result : result
      })
    }
    
    // Apply pagination
    const page = filters.page || 1
    const limit = filters.limit || 10
    const start = (page - 1) * limit
    const paginatedProducts = filteredProducts.slice(start, start + limit)
    
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: paginatedProducts,
          currentPage: page,
          firstPage: 1,
          lastPage: Math.ceil(filteredProducts.length / limit),
          pageSize: limit,
          totalRecords: filteredProducts.length,
        })
      }, 500)
    })
  }

  private static async getMockProduct(id: string): Promise<Product> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          uuid: id,
          brandUUID: 'brand-1',
          brandName: 'Test Brand',
          categoryId: 1,
          categoryName: 'Test Category',
          subCategoryId: 1,
          subCategoryName: 'Test SubCategory',
          galenicFormId: 1,
          manufacturingContinentId: 1,
          toValidate: false,
          images: [
            {
              UUID: 'img-1',
              imageType: 'front',
              imageUrl: 'https://via.placeholder.com/300x400/4F46E5/ffffff?text=Front',
              imageBlurHash: 'LGG]~V?b~q?b~q?b~q?b~q?b~q?b'
            },
            {
              UUID: 'img-2',
              imageType: 'back',
              imageUrl: 'https://via.placeholder.com/300x400/10B981/ffffff?text=Back',
              imageBlurHash: 'LGG]~V?b~q?b~q?b~q?b~q?b~q?b'
            },
            {
              UUID: 'img-3',
              imageType: 'detailed',
              imageUrl: 'https://via.placeholder.com/300x400/F59E0B/ffffff?text=Detailed',
              imageBlurHash: 'LGG]~V?b~q?b~q?b~q?b~q?b~q?b'
            }
          ],
          ingredients: [],
          certifications: [],
        })
      }, 500)
    })
  }
}